/**
 * PATH: packages/email-hub/src/feature/components/EmailVisibilityToggle.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-VISIBILITYTOGGLE-001
 * PURPOSE: Two buttons, Public and Private, for one email. Private keeps the
 *          message inside its mailbox while AI still files and routes it.
 * NOTES FOR FUTURE AI: The reader passes a PATCH writer; compose passes
 *   local state. Only the mailbox owner may flip a stored email, so the
 *   reader hides the control when the viewer does not own the row.
 */
import { useState, type ReactElement } from 'react'
import { Globe, Lock } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import type { EmailVisibility } from '../hooks/useEmails.types'

export interface EmailVisibilityToggleProps {
  value: EmailVisibility | undefined
  /** Resolve the new value. Reject to leave the control on the old one. */
  onChange: (next: EmailVisibility) => void | Promise<void>
  disabled?: boolean
  /**
   * 'lane' matches the reader lane chip geometry (24px row, 10px text) and
   * shows the words only from 900px up, so the Filed To lane keeps one row
   * and never scrolls at the 660px acceptance width. 'md' is the compose row.
   */
  size?: 'lane' | 'md'
  className?: string
}

const OPTIONS: Array<{
  value: EmailVisibility
  label: string
  title: string
  Icon: typeof Globe
}> = [
  {
    value: 'public',
    label: 'Public',
    title: 'Teammates on the job, board or bill can open this email.',
    Icon: Globe,
  },
  {
    value: 'private',
    label: 'Private',
    title: 'Stays in your mailbox. AI still files and routes it, but others cannot open it.',
    Icon: Lock,
  },
]

export function normaliseVisibility(value: unknown): EmailVisibility {
  return value === 'private' ? 'private' : 'public'
}

/**
 * Renders the Public | Private pair.
 *
 * Inputs: the current value, a change writer, optional disabled flag.
 * Output: a segmented control; the selected side is pressed.
 * Failure mode: a rejected onChange leaves the old side selected.
 */
export function EmailVisibilityToggle({
  value,
  onChange,
  disabled = false,
  size = 'lane',
  className,
}: EmailVisibilityToggleProps): ReactElement {
  const current = normaliseVisibility(value)
  const [busy, setBusy] = useState(false)

  const choose = async (next: EmailVisibility) => {
    if (busy || disabled || next === current) return
    setBusy(true)
    try {
      await onChange(next)
    } catch {
      // The writer owns the feedback (toast). The pressed side simply stays
      // on the stored value, which the parent did not change.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Who can see this email"
      data-testid="email-visibility-toggle"
      data-value={current}
      className={cn(
        // min-w-0, not shrink-0: inside a lane the pair clips with the rest of
        // the chip run instead of forcing the lane to scroll sideways.
        // ring-inset, not border: a border adds 2px to the 24px lane row and
        // the header budget is measured to the pixel.
        'inline-flex min-w-0 overflow-hidden rounded-full ring-1 ring-inset ring-gray-200 dark:ring-gray-700',
        (busy || disabled) && 'opacity-60',
        className,
      )}
    >
      {OPTIONS.map(({ value: option, label, title, Icon }) => {
        const selected = option === current
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={label}
            title={title}
            disabled={busy || disabled}
            data-testid={`email-visibility-${option}`}
            onClick={() => void choose(option)}
            className={cn(
              // whitespace-nowrap: a wrapped label grows past the lane row
              // floor and takes the whole row with it (laneControl.ts).
              'inline-flex items-center gap-1 whitespace-nowrap font-medium leading-none transition-colors',
              size === 'lane'
                ? 'min-h-6 px-2 py-0.5 text-[10px] pointer-coarse:min-h-11'
                : 'px-3 py-1.5 text-xs',
              selected
                ? option === 'private'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800',
            )}
          >
            <Icon className="h-3 w-3 shrink-0" aria-hidden />
            <span className={size === 'lane' ? 'hidden min-[900px]:inline' : undefined}>{label}</span>
          </button>
        )
      })}
    </div>
  )
}

export default EmailVisibilityToggle
